import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { inquiryApi, type Inquiry } from "@/lib/api";
import { formatDateTime } from "@/lib/date";
import { getServiceBadge, getStatusBadge } from "@/lib/constants";
import Footer from "@/pages/home/components/Footer";
import ScrollReveal from "@/components/ScrollReveal";

const STATUS_OPTIONS = ["접수", "처리중", "완료"] as const;

const PAGE_SIZE = 15;

export default function InquiryAdminPage() {
  const navigate = useNavigate();
  const [inquiries, setInquiries] = useState<Inquiry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<number | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState<string>("전체");

  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));

  const fetchInquiries = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const { data, total } = await inquiryApi.list(page, PAGE_SIZE, statusFilter);
      setInquiries(data || []);
      setTotalCount(total);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "문의 목록을 불러오지 못했습니다.";
      setError(msg);
      setInquiries([]);
    } finally {
      setLoading(false);
    }
  }, [page, statusFilter]);

  useEffect(() => {
    fetchInquiries();
  }, [fetchInquiries]);

  useEffect(() => {
    setPage(1);
  }, [statusFilter]);

  const toggleExpand = (id: number) => {
    setExpandedId((prev) => (prev === id ? null : id));
  };

  const handleDelete = async (id: number) => {
    setActionLoading(true);
    try {
      await inquiryApi.remove(id);
      setDeleteConfirmId(null);
      if (expandedId === id) setExpandedId(null);
      fetchInquiries();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "삭제에 실패했습니다.";
      setError(msg);
    } finally {
      setActionLoading(false);
    }
  };

  const handleStatusChange = async (id: number, newStatus: string) => {
    setActionLoading(true);
    try {
      await inquiryApi.updateStatus(id, newStatus);
      fetchInquiries();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "상태 변경에 실패했습니다.";
      setError(msg);
    } finally {
      setActionLoading(false);
    }
  };

  const handleBulkDelete = async () => {
    setActionLoading(true);
    try {
      await inquiryApi.bulkRemove(statusFilter);
      setExpandedId(null);
      fetchInquiries();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "일괄 삭제에 실패했습니다.";
      setError(msg);
    } finally {
      setActionLoading(false);
    }
  };

  const statusCounts = inquiries.reduce(
    (acc, inq) => {
      acc[inq.status || "접수"] = (acc[inq.status || "접수"] || 0) + 1;
      return acc;
    },
    {} as Record<string, number>
  );

  return (
    <>
      <main className="min-h-screen bg-background-50 pt-20">
        <section className="py-16 md:py-24">
          <div className="max-w-6xl mx-auto px-6 md:px-10">
            <div className="mb-6 flex items-center justify-between flex-wrap gap-3">
              <button
                onClick={() => navigate("/admin/dashboard")}
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md border border-background-200 hover:border-primary-400 hover:bg-primary-50 text-foreground-600 hover:text-primary-700 text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-arrow-left-line w-4 h-4 flex items-center justify-center"></i>
                대시보드로 돌아가기
              </button>
              <button
                onClick={fetchInquiries}
                className="inline-flex items-center gap-2 px-3 py-2 rounded-md text-sm text-foreground-500 hover:text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-refresh-line w-4 h-4 flex items-center justify-center"></i>
                새로고침
              </button>
            </div>

            <div className="text-center mb-14">
              <ScrollReveal direction="up" delay={100}>
                <div className="inline-flex items-center gap-2 text-primary-700 text-xs tracking-[0.3em] uppercase font-medium mb-4">
                  <span className="w-8 h-px bg-primary-500"></span>
                  Inquiry Management
                </div>
                <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground-950">
                  문의 게시판 관리
                </h1>
                <p className="mt-4 text-foreground-700 text-base max-w-xl mx-auto">
                  고객 문의를 확인하고 상태를 변경하거나 삭제할 수 있습니다.
                </p>
              </ScrollReveal>
            </div>

            {error && (
              <div className="mb-6 p-4 rounded-lg bg-accent-50 border border-accent-200 flex items-start gap-3">
                <i className="ri-error-warning-line text-accent-600 w-5 h-5 flex items-center justify-center mt-0.5"></i>
                <div className="flex-1">
                  <p className="text-accent-800 text-sm">{error}</p>
                </div>
                <button
                  onClick={() => setError("")}
                  className="text-accent-500 hover:text-accent-700 w-5 h-5 flex items-center justify-center cursor-pointer"
                >
                  <i className="ri-close-line w-4 h-4 flex items-center justify-center"></i>
                </button>
              </div>
            )}

            {/* Stats & Filters Bar */}
            <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xs text-foreground-500 font-medium tracking-wide uppercase">
                  필터:
                </span>
                {["전체", "접수", "처리중", "완료"].map((s) => (
                  <button
                    key={s}
                    onClick={() => setStatusFilter(s)}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors cursor-pointer whitespace-nowrap border ${
                      statusFilter === s
                        ? "border-primary-300 bg-primary-50 text-primary-700"
                        : "border-background-200 bg-background-50 text-foreground-600 hover:border-primary-400 hover:text-primary-700"
                    }`}
                  >
                    {s}
                    {s !== "전체" && statusCounts[s] ? ` (${statusCounts[s]})` : ""}
                  </button>
                ))}
              </div>

              <div className="flex items-center gap-3">
                <span className="text-sm text-foreground-500">
                  총 <strong className="text-foreground-800">{totalCount}</strong>건
                </span>
                {totalCount > 0 && (
                  <button
                    onClick={() => {
                      if (confirm(
                        statusFilter === "전체"
                          ? "정말로 모든 문의를 삭제하시겠습니까? 이 작업은 되돌릴 수 없습니다."
                          : `정말로 "${statusFilter}" 상태의 모든 문의를 삭제하시겠습니까?`
                      )) {
                        handleBulkDelete();
                      }
                    }}
                    disabled={actionLoading}
                    className="px-3 py-1.5 rounded-md border border-accent-300 bg-background-50 text-accent-700 hover:bg-accent-50 text-xs font-medium transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50 flex items-center gap-1"
                  >
                    <i className="ri-delete-bin-line w-3 h-3 flex items-center justify-center"></i>
                    {statusFilter === "전체" ? "전체 삭제" : `"${statusFilter}" 삭제`}
                  </button>
                )}
              </div>
            </div>

            {loading && (
              <div className="flex flex-col items-center justify-center py-24 gap-4">
                <i className="ri-loader-4-line animate-spin text-3xl text-primary-500 w-8 h-8 flex items-center justify-center"></i>
                <span className="text-foreground-500 text-sm">문의 목록을 불러오는 중...</span>
              </div>
            )}

            {!loading && !error && inquiries.length === 0 && (
              <div className="text-center py-16">
                <div className="inline-flex flex-col items-center gap-3 p-8 rounded-lg bg-background-100 border border-background-200">
                  <i className="ri-inbox-line text-3xl text-foreground-400 w-8 h-8 flex items-center justify-center"></i>
                  <p className="text-foreground-600 text-sm">
                    {statusFilter === "전체"
                      ? "등록된 문의가 없습니다."
                      : `"${statusFilter}" 상태의 문의가 없습니다.`}
                  </p>
                </div>
              </div>
            )}

            {!loading && inquiries.length > 0 && (
              <>
                <div className="w-full rounded-lg border border-background-200/70 overflow-hidden bg-background-50">
                  <div className="hidden md:grid grid-cols-12 gap-4 px-5 py-3.5 bg-background-100/60 border-b border-background-200/70 text-xs font-medium text-foreground-500 tracking-wide uppercase">
                    <div className="col-span-1 text-center">No.</div>
                    <div className="col-span-2">서비스</div>
                    <div className="col-span-2">작성자</div>
                    <div className="col-span-3">문의 내용</div>
                    <div className="col-span-1 text-center">상태</div>
                    <div className="col-span-2 text-right">날짜</div>
                    <div className="col-span-1 text-center">관리</div>
                  </div>

                  {inquiries.map((inq, idx) => {
                    const isExpanded = expandedId === inq.id;
                    const badgeClass = getServiceBadge(inq.service);

                    return (
                      <div key={inq.id}>
                        <div
                          onClick={() => toggleExpand(inq.id)}
                          className="grid grid-cols-12 gap-4 px-5 py-4 border-b border-background-100 hover:bg-background-50/60 cursor-pointer transition-colors items-center"
                        >
                          <div className="col-span-1 text-center text-xs text-foreground-400 font-mono">
                            {inq.id}
                          </div>
                          <div className="col-span-3 md:col-span-2">
                            <span
                              className={`inline-block px-2.5 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${badgeClass}`}
                            >
                              {inq.service}
                            </span>
                          </div>
                          <div className="col-span-5 md:col-span-2 text-sm text-foreground-800 font-medium truncate">
                            {inq.name || "익명"}
                          </div>
                          <div className="col-span-3 md:col-span-3 text-sm text-foreground-600 truncate">
                            {inq.message?.slice(0, 35) || "(내용 없음)"}
                            {inq.message?.length > 35 ? "..." : ""}
                          </div>
                          <div className="col-span-3 md:col-span-1 flex justify-start md:justify-center items-center">
                            <select
                              value={inq.status || "접수"}
                              onChange={(e) => {
                                e.stopPropagation();
                                handleStatusChange(inq.id, e.target.value);
                              }}
                              onClick={(e) => e.stopPropagation()}
                              disabled={actionLoading}
                              className={`px-2 py-0.5 rounded-full text-[11px] font-medium cursor-pointer border-0 outline-none appearance-none text-center ${getStatusBadge(inq.status)} disabled:opacity-50`}
                            >
                              {STATUS_OPTIONS.map((s) => (
                                <option key={s} value={s}>
                                  {s}
                                </option>
                              ))}
                            </select>
                          </div>
                          <div className="hidden md:block md:col-span-2 text-right text-xs text-foreground-400 whitespace-nowrap">
                            {formatDateTime(inq.created_at)}
                          </div>
                          <div className="hidden md:flex md:col-span-1 justify-center items-center">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setDeleteConfirmId(inq.id);
                              }}
                              disabled={actionLoading}
                              className="w-8 h-8 rounded-md border border-accent-200 bg-background-50 text-accent-600 hover:bg-accent-50 transition-colors cursor-pointer flex items-center justify-center disabled:opacity-50"
                              title="삭제"
                            >
                              <i className="ri-delete-bin-line w-4 h-4 flex items-center justify-center"></i>
                            </button>
                          </div>
                        </div>

                        {isExpanded && (
                          <div className="px-5 py-5 bg-background-100/40 border-b border-background-200/40">
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
                              <div className="flex gap-2">
                                <span className="text-foreground-400 w-16 flex-shrink-0">이름</span>
                                <span className="text-foreground-900 font-medium">{inq.name || "익명"}</span>
                              </div>
                              <div className="flex gap-2">
                                <span className="text-foreground-400 w-16 flex-shrink-0">연락처</span>
                                <span className="text-foreground-900">{inq.phone || "-"}</span>
                              </div>
                              <div className="flex gap-2">
                                <span className="text-foreground-400 w-16 flex-shrink-0">이메일</span>
                                <span className="text-foreground-900">{inq.email || "-"}</span>
                              </div>
                              <div className="flex gap-2">
                                <span className="text-foreground-400 w-16 flex-shrink-0">회사</span>
                                <span className="text-foreground-900">{inq.company || "-"}</span>
                              </div>
                              <div className="flex gap-2">
                                <span className="text-foreground-400 w-16 flex-shrink-0">서비스</span>
                                <span className="text-foreground-900">{inq.service}</span>
                              </div>
                              <div className="flex gap-2">
                                <span className="text-foreground-400 w-16 flex-shrink-0">스크린</span>
                                <span className="text-foreground-900">{inq.screen || "-"}</span>
                              </div>
                              <div className="flex gap-2">
                                <span className="text-foreground-400 w-16 flex-shrink-0">현장</span>
                                <span className="text-foreground-900">{inq.site || "-"}</span>
                              </div>
                              <div className="flex gap-2 items-center">
                                <span className="text-foreground-400 w-16 flex-shrink-0">상태</span>
                                <select
                                  value={inq.status || "접수"}
                                  onChange={(e) => handleStatusChange(inq.id, e.target.value)}
                                  disabled={actionLoading}
                                  className={`px-2 py-0.5 rounded-full text-xs font-medium cursor-pointer border-0 outline-none ${getStatusBadge(inq.status)} disabled:opacity-50`}
                                >
                                  {STATUS_OPTIONS.map((s) => (
                                    <option key={s} value={s}>{s}</option>
                                  ))}
                                </select>
                              </div>
                              <div className="flex gap-2">
                                <span className="text-foreground-400 w-16 flex-shrink-0">접수일</span>
                                <span className="text-foreground-900">{formatDateTime(inq.created_at)}</span>
                              </div>
                            </div>
                            <div className="mt-4 p-4 rounded-md bg-background-50 border border-background-200/60">
                              <p className="text-sm text-foreground-800 whitespace-pre-wrap leading-relaxed">
                                {inq.message || "(내용 없음)"}
                              </p>
                            </div>
                            <div className="mt-4 pt-4 border-t border-background-200/70 flex justify-end gap-2">
                              <button
                                onClick={() => setDeleteConfirmId(inq.id)}
                                disabled={actionLoading}
                                className="px-3 py-1.5 rounded-md border border-accent-300 bg-background-50 text-accent-700 hover:bg-accent-50 text-xs font-medium transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50 flex items-center gap-1"
                              >
                                <i className="ri-delete-bin-line w-3 h-3 flex items-center justify-center"></i>
                                삭제
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                {/* Delete Confirmation Modal */}
                {deleteConfirmId !== null && (
                  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
                    <div className="bg-background-50 rounded-xl border border-background-200/80 shadow-lg p-6 mx-4 max-w-sm w-full">
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 rounded-full bg-accent-100 flex items-center justify-center flex-shrink-0">
                          <i className="ri-error-warning-line text-accent-600 w-5 h-5 flex items-center justify-center"></i>
                        </div>
                        <div>
                          <h3 className="text-lg font-bold text-foreground-950">문의 삭제</h3>
                          <p className="text-sm text-foreground-600 mt-1">
                            정말로 이 문의를 삭제할까요? 이 작업은 되돌릴 수 없어요.
                          </p>
                        </div>
                      </div>
                      <div className="flex justify-end gap-2 mt-6">
                        <button
                          onClick={() => setDeleteConfirmId(null)}
                          disabled={actionLoading}
                          className="px-4 py-2 rounded-md border border-background-200 bg-background-50 text-foreground-700 text-sm font-medium hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
                        >
                          취소
                        </button>
                        <button
                          onClick={() => handleDelete(deleteConfirmId)}
                          disabled={actionLoading}
                          className="px-4 py-2 rounded-md bg-accent-500 hover:bg-accent-600 text-background-50 text-sm font-medium transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50 flex items-center gap-2"
                        >
                          {actionLoading ? (
                            <>
                              <i className="ri-loader-4-line animate-spin w-4 h-4 flex items-center justify-center"></i>
                              삭제 중...
                            </>
                          ) : (
                            "삭제"
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {totalPages > 1 && (
                  <div className="flex items-center justify-center gap-2 mt-8">
                    <button
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                      disabled={page === 1}
                      className="w-9 h-9 rounded-md border border-background-200 bg-background-50 text-foreground-700 hover:bg-background-100 disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center cursor-pointer transition-colors"
                    >
                      <i className="ri-arrow-left-s-line w-4 h-4 flex items-center justify-center"></i>
                    </button>
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                      <button
                        key={p}
                        onClick={() => setPage(p)}
                        className={`w-9 h-9 rounded-md text-sm font-medium cursor-pointer whitespace-nowrap transition-colors ${
                          p === page
                            ? "bg-primary-500 text-background-50"
                            : "border border-background-200 bg-background-50 text-foreground-700 hover:bg-background-100"
                        }`}
                      >
                        {p}
                      </button>
                    ))}
                    <button
                      onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                      disabled={page === totalPages}
                      className="w-9 h-9 rounded-md border border-background-200 bg-background-50 text-foreground-700 hover:bg-background-100 disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center cursor-pointer transition-colors"
                    >
                      <i className="ri-arrow-right-s-line w-4 h-4 flex items-center justify-center"></i>
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}